/**
 * 足迹 —— 董事长要的是「激发成就感」，所以这一页刻意不做成学习流水账。
 *
 * 一屏之内回答四个问题，一个比一个具体：
 *   我一共学了多久 → 我连了多少天 → 我解锁了什么 → 我走过了哪些节点。
 * 全部是只增不减的量。列「今天只学了 42 分钟」是打击，列「累计 42 小时」是燃料。
 */
import { useEffect, useState } from 'react'
import { trailApi, fmtSpan, mdLabel, type TrailSummary, type Badge } from '../lib/trail'

const card: React.CSSProperties = {
  background: 'var(--ps-surface)', borderRadius: 20, border: '1px solid var(--ps-line)',
  boxShadow: 'var(--ps-shadow-sm)', padding: 18,
}

export default function Trail() {
  const [s, setS] = useState<TrailSummary | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => { void trailApi.summary().then((r) => r.ok ? setS(r.data) : setErr(r.message)) }, [])

  if (err) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--ps-text2)' }}>读不到足迹：{err}</div>
  if (!s) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--ps-text3)' }}>正在翻记录…</div>

  return (
    <div className="safe-b" style={{
      display: 'grid', gap: 14, padding: '8px 16px 28px', maxWidth: 520, margin: '0 auto', width: '100%',
    }}>
      <Total s={s} />
      <div className="rise rise-2"><Stats s={s} /></div>
      <div className="rise rise-3"><Heat heat={s.heat} /></div>
      <div className="rise rise-4"><Badges s={s} /></div>
      {s.passed.length > 0 && <div className="rise rise-4"><Passed list={s.passed} /></div>}
    </div>
  )
}

/* ── 累计：全页最大的那个数 ───────────────────── */
function Total({ s }: { s: TrailSummary }) {
  const t = fmtSpan(s.totalMinutes)
  return (
    <section className="rise rise-1" style={{
      ...card, padding: '22px 20px 20px',
      background: 'linear-gradient(140deg, var(--ps-accent-soft), var(--ps-surface) 78%)',
      borderColor: 'var(--ps-accent-line)',
    }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ps-accent)', letterSpacing: '.04em' }}>
        累计学习
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        <span className="num" style={{
          fontSize: 'clamp(64px, 22vw, 96px)', fontWeight: 900, lineHeight: .92,
          letterSpacing: '-.05em', fontVariantNumeric: 'tabular-nums',
        }}>{t.big}</span>
        <span style={{ fontSize: 26, fontWeight: 800 }}>{t.unit}</span>
        {t.tail && <span style={{ fontSize: 19, fontWeight: 700, color: 'var(--ps-text2)' }}>{t.tail}</span>}
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 14.5, color: 'var(--ps-text2)', fontWeight: 600 }}>
        {s.since ? <>从 {mdLabel(s.since)} 起 · 有 {s.activeDays} 天坐了下来</> : '还没有记录，今天就是第一天'}
      </p>
      {/* 离屏模式切走也照常计时，全靠自觉 —— 所以要留痕。写成中性的事实，
          不加判断：一直离屏也可能真的在看纸质书。 */}
      {s.modes.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginTop: 9, flexWrap: 'wrap' }}>
          {s.modes.map((m) => (
            <span key={m.mode} style={{ fontSize: 13, fontWeight: 700, color: 'var(--ps-text3)' }}>
              {m.mode === 'onscreen' ? '📱 专注' : '📖 离屏'}
              <b className="num" style={{ marginLeft: 5, color: 'var(--ps-accent)' }}>{m.pct}%</b>
            </span>
          ))}
        </div>
      )}
    </section>
  )
}

/* ── 三块：连续是最能拽住人的那个数 ───────────── */
function Stats({ s }: { s: TrailSummary }) {
  const items = [
    { k: '当前连续', v: s.streak.current, u: '天', hot: s.streak.current > 0 },
    { k: '最长连续', v: s.streak.best, u: '天', hot: false },
    { k: '达标天数', v: s.doneDays, u: '天', hot: false },
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
      {items.map((i) => (
        <div key={i.k} style={{ ...card, padding: '15px 10px', textAlign: 'center' }}>
          <div className="num" style={{
            fontSize: 34, fontWeight: 900, lineHeight: 1, fontVariantNumeric: 'tabular-nums',
            color: i.hot ? 'var(--ps-accent)' : 'var(--ps-text)',
          }}>{i.v}<span style={{ fontSize: 15, marginLeft: 2, fontWeight: 700 }}>{i.u}</span></div>
          <div style={{ fontSize: 12.5, color: 'var(--ps-text3)', marginTop: 7, fontWeight: 700 }}>{i.k}</div>
        </div>
      ))}
    </div>
  )
}

/* ── 热力图：18 周。铺开看得见「这一片是我填的」 ── */
function Heat({ heat }: { heat: TrailSummary['heat'] }) {
  if (!heat.length) return null
  // 按周分列，每列七格从周一到周日 —— 先把首周缺的几天补空，日子才对得上行
  const first = new Date(`${heat[0]!.d}T00:00:00`)
  const pad = (first.getDay() + 6) % 7
  const cells: (typeof heat[0] | null)[] = [...Array(pad).fill(null), ...heat]
  const weeks: (typeof cells)[] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))

  const tone = (c: typeof heat[0] | null) => {
    if (!c) return 'transparent'
    if (c.m <= 0) return 'var(--ps-line)'
    if (c.t > 0 && c.m >= c.t) return 'var(--ps-accent)'
    const r = c.t > 0 ? c.m / c.t : 1
    return r >= .5 ? 'var(--ps-tint)' : 'var(--ps-accent-line)'
  }
  return (
    <section style={card}>
      <Head title="每一格都是一天" right={`近 ${weeks.length} 周`} />
      {/* 格子跟着卡片宽度走：只有两周记录时 13px 的小方块散在一大片空白里，
          看着像是没渲染完。用 1fr 自适应，周数少就长成大块，周数多就自动收窄。
          周数很多时横向滚动，绝不撑破页面。 */}
      <div style={{ overflowX: 'auto', paddingBottom: 4, marginTop: 14 }}>
        <div style={{
          display: 'grid', gap: 5,
          /* 上限 22px：只有两周记录时若放任 1fr 撑开，格子会长成 40px 的大方块，
             一大片空白挤在右边，反而像坏掉了。居中放置，多寡都成立。 */
          gridTemplateColumns: `repeat(${weeks.length}, minmax(13px, 22px))`,
          justifyContent: 'center',
        }}>
          {weeks.map((w, i) => (
            <div key={i} style={{ display: 'grid', gap: 5 }}>
              {Array.from({ length: 7 }, (_, j) => {
                const c = w[j] ?? null
                return <i key={j} title={c ? `${c.d} · ${c.m} 分钟` : ''} style={{
                  aspectRatio: '1', borderRadius: 5, background: tone(c), display: 'block',
                }} />
              })}
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--ps-text3)', fontWeight: 600 }}>
        <span>没学</span>
        {['var(--ps-line)', 'var(--ps-accent-line)', 'var(--ps-tint)', 'var(--ps-accent)'].map((c) => (
          <i key={c} style={{ width: 13, height: 13, borderRadius: 4, background: c, display: 'block' }} />
        ))}
        <span>达标</span>
      </div>
    </section>
  )
}

/* ── 徽章墙 ───────────────────────────────────── */
function Badges({ s }: { s: TrailSummary }) {
  return (
    <section style={card}>
      <Head title="解锁的成就" right={`${s.unlocked} / ${s.total}`} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(102px, 1fr))', gap: 9, marginTop: 12 }}>
        {s.badges.map((b) => <BadgeCell key={b.id} b={b} />)}
      </div>
    </section>
  )
}

function BadgeCell({ b }: { b: Badge }) {
  const got = !!b.unlockedAt
  return (
    <div title={b.desc} style={{
      padding: '13px 8px 12px', borderRadius: 15, textAlign: 'center',
      /* 定高 + 纵向居中：未解锁那格要放整句说明（「连续 3 天达标」），
         不定高就会比已解锁的高出一截，整墙参差不齐、末行还被裁掉 */
      minHeight: 118, display: 'flex', flexDirection: 'column', justifyContent: 'center',
      background: got ? 'var(--ps-accent-soft)' : 'var(--ps-surface2)',
      border: `1px solid ${got ? 'var(--ps-accent-line)' : 'var(--ps-line)'}`,
      /* 未解锁的压暗但不隐藏 —— 看得见下一枚长什么样，才有得追 */
      opacity: got ? 1 : .45,
    }}>
      <div style={{ fontSize: 27, lineHeight: 1.15, filter: got ? 'none' : 'grayscale(1)' }}>{b.emoji}</div>
      <div style={{ fontSize: 13.5, fontWeight: 800, marginTop: 6, color: 'var(--ps-text)' }}>{b.name}</div>
      <div style={{ fontSize: 11.5, marginTop: 4, fontWeight: 700, lineHeight: 1.45, color: got ? 'var(--ps-accent)' : 'var(--ps-text2)' }}>
        {got ? mdLabel(b.unlockedAt!) : b.desc}
      </div>
    </div>
  )
}

/* ── 走过的节点 ───────────────────────────────── */
const KIND: Record<string, { t: string; e: string }> = {
  exam: { t: '正式考试', e: '🎯' }, mock: { t: '模考', e: '📝' }, checkpoint: { t: '节点', e: '🚩' },
}
function Passed({ list }: { list: TrailSummary['passed'] }) {
  return (
    <section style={card}>
      <Head title="走过的节点" right={`${list.length} 个`} />
      <div style={{ display: 'grid', gap: 2, marginTop: 8 }}>
        {list.map((m) => (
          <div key={m.day + m.title} style={{
            display: 'flex', alignItems: 'center', gap: 11, padding: '11px 2px',
            borderBottom: '1px solid var(--ps-line)',
          }}>
            <span style={{ fontSize: 19 }}>{KIND[m.kind]?.e ?? '·'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--ps-text)' }}>{m.title}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ps-text3)', marginTop: 2, fontWeight: 600 }}>
                {mdLabel(m.day)}{m.subject ? ` · ${m.subject}` : ''}
              </div>
            </div>
            <span style={{ fontSize: 17, color: 'var(--ps-accent)' }}>✓</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function Head({ title, right }: { title: string; right?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
      <h2 style={{ margin: 0, fontSize: 17.5, fontWeight: 800, letterSpacing: '-.01em' }}>{title}</h2>
      {right && <span className="num" style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ps-text3)' }}>{right}</span>}
    </div>
  )
}
